import { useTranslate } from "@opensea/next-translate"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { ChainMedia } from "@/components/common/media/ChainMedia.react"
import { CollectionMedia } from "@/components/common/media/CollectionMedia"
import { StackedAssetMedia } from "@/components/common/media/StackedAssetMedia"
import type { CancelOrder_metadata$key } from "@/lib/graphql/__generated__/CancelOrder_metadata.graphql"
import type { CancelOrder_receipt$key } from "@/lib/graphql/__generated__/CancelOrder_receipt.graphql"
import { useConnectedChain } from "@/providers/Wallet/selectors"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type CancelOrderProps = {
  receipt: CancelOrder_receipt$key | null | undefined
  metadata: CancelOrder_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function CancelOrder({
  receipt: receiptKey,
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: CancelOrderProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const receipt = useFragment<CancelOrder_receipt$key>(
    graphql`
      fragment CancelOrder_receipt on TransactionReceiptType {
        transactionFee {
          unit
          symbol
          usd
        }
      }
    `,
    receiptKey,
  )

  const metadata = useFragment<CancelOrder_metadata$key>(
    graphql`
      fragment CancelOrder_metadata on TransactionMetadataType {
        sentAssetQuantities {
          asset {
            displayName
            tokenStandard
            ...StackedAssetMedia_assets
          }
        }
        receivedAssetQuantities {
          asset {
            displayName
            tokenStandard
            ...StackedAssetMedia_assets
          }
        }
        collections {
          name
          ...CollectionMedia_collection
        }
      }
    `,
    metadataKey,
  )

  const chain = useConnectedChain() ?? "ETHEREUM"

  const isListing =
    metadata.sentAssetQuantities.findIndex(
      aq =>
        aq.asset.tokenStandard === "ERC721" ||
        aq.asset.tokenStandard === "ERC1155",
    ) !== -1

  // At most one NFT associated with the cancelled listing/offer
  const nftAssetQuantity = isListing
    ? metadata.sentAssetQuantities.at(0)
    : metadata.receivedAssetQuantities.at(0)

  // Only one collection associated with the cancelled listing/offer
  const collection = metadata.collections.at(0)

  const getTitle = () => {
    if (isListing) {
      return t("txItem.cancelOrder.title.listing", "Cancel listing")
    }
    if (nftAssetQuantity) {
      return t("txItem.cancelOrder.title.offer", "Cancel offer")
    }
    return t(
      "txItem.cancelOrder.title.collectionOffer",
      "Cancel collection offer",
    )
  }

  const isComplete = receipt && transactionHash
  const isPending = !receipt && transactionHash

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        {nftAssetQuantity?.asset ? (
          <StackedAssetMedia assets={[nftAssetQuantity.asset]} />
        ) : collection ? (
          <CollectionMedia collection={collection} />
        ) : (
          <ChainMedia chain={chain} />
        )}
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>{getTitle()}</TxItem.Title>
        <TxItem.Description>
          {nftAssetQuantity?.asset.displayName ?? collection?.name}
        </TxItem.Description>
      </TxItem.Content>
      {isComplete ? (
        <TxItem.Value
          symbol={receipt.transactionFee.symbol}
          unit={receipt.transactionFee.unit}
          usd={receipt.transactionFee.usd}
        />
      ) : isPending ? (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      ) : (
        <TxItem.Requested />
      )}
    </TxItem>
  )
}
